import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Users, 
  FileSpreadsheet, 
  Link2, 
  Smartphone, 
  Clock, 
  Building2, 
  Shield,
  Zap,
  BarChart3
} from "lucide-react";
import { SiQuickbooks } from "react-icons/si";
import freshOrderLogo from "@assets/Logo_-new_clear_1764787161621.png";
import videoThumbnail from "@assets/thumbnail_logo_on_white_1767972845666.png";

export default function AppInfo() {
  const [videoPlaying, setVideoPlaying] = useState(false);
  
  useEffect(() => {
    document.title = "FreshOrder | Order Management for Produce Distribution";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src={freshOrderLogo} 
              alt="FreshOrder" 
              className="h-12 w-auto"
            />
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">FreshOrder</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Multi-tenant order management platform for produce and food distribution businesses
          </p>
          
          {/* Explainer Video - Click to Play */}
          <div className="relative w-full max-w-3xl mx-auto rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white aspect-video">
            {videoPlaying ? (
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/XlkPsL3HjHg?autoplay=1"
                title="FreshOrder Explainer Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <button
                onClick={() => setVideoPlaying(true)}
                className="w-full h-full relative group cursor-pointer"
                aria-label="Play video"
              >
                <img
                  src={videoThumbnail}
                  alt="FreshOrder Explainer Video"
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </button>
            )}
          </div>
        </header>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Description</h2>
          <div className="prose prose-gray max-w-none space-y-4">
            <p className="text-gray-600 text-lg leading-relaxed">
              FreshOrder is a powerful SaaS platform designed to streamline order management for produce distributors, food suppliers, and wholesale businesses. It replaces fragmented order-taking processes with a unified, mobile-first system that customers love and staff can manage effortlessly.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Beyond order collection, FreshOrder serves as a complete procurement support tool. The interactive items list organizes products by category with smart features designed for warehouse operations: ordered items are automatically sorted and grouped, quantities are aggregated across all customers, and items can be auto-labeled with status indicators like "short" to flag fulfillment issues. Products can include images, descriptions, and custom visibility settings per customer—giving sellers full control over what each customer sees.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              This tight integration between customer ordering and back-office procurement means sellers can see real-time demand, plan sourcing accordingly, and fulfill orders with confidence—all from a single platform.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">What it does</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard 
              icon={<Smartphone className="w-5 h-5" />}
              title="Mobile-First Customer Ordering"
              description="Customers place orders via personalized links—no app downloads required. Phone number lookup makes repeat ordering instant."
            />
            <FeatureCard 
              icon={<BarChart3 className="w-5 h-5" />}
              title="Real-Time Dashboard"
              description="Auto-refreshing seller dashboard with order statistics, filtering, and a three-stage workflow (pending → reviewed → delivered)."
            />
            <FeatureCard 
              icon={<FileSpreadsheet className="w-5 h-5" />}
              title="Excel Exports"
              description="Generate warehouse-optimized packing slips and aggregated item quantity reports for efficient order fulfillment."
            />
            <FeatureCard 
              icon={<Users className="w-5 h-5" />}
              title="Customer Management"
              description="Full CRUD for customer accounts with custom URL generation. Each customer gets their own shareable ordering link."
            />
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Key Features</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard 
              icon={<SiQuickbooks className="w-5 h-5" />}
              title="QuickBooks Integration"
              description="OAuth-based sync for customers and products. Create invoices directly from orders with intelligent matching."
              highlight
            />
            <FeatureCard 
              icon={<Building2 className="w-5 h-5" />}
              title="Multi-Tenant Architecture"
              description="Each organization gets isolated data, custom branding, and their own subdomain—all from one platform."
              highlight
            />
            <FeatureCard 
              icon={<Shield className="w-5 h-5" />}
              title="Secure PIN Authentication"
              description="4-digit PIN-based customer login with magic link recovery. Role-based admin permissions for staff."
            />
            <FeatureCard 
              icon={<Link2 className="w-5 h-5" />}
              title="Smart Data Sync"
              description="Import customers and products from QuickBooks. Intelligent name matching prevents duplicates."
            />
            <FeatureCard 
              icon={<Clock className="w-5 h-5" />}
              title="Order Period Scheduling"
              description="Automated order periods with configurable cutoff times. Notes and special instructions supported."
            />
            <FeatureCard 
              icon={<Zap className="w-5 h-5" />}
              title="Twilio & Gmail Integration"
              description="SMS reminders and email notifications keep customers informed about their orders."
            />
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why it matters</h2>
          <div className="bg-[#2B5397]/5 border border-[#2B5397]/20 rounded-xl p-6">
            <ul className="space-y-4 text-gray-700">
              <li className="flex gap-3">
                <span className="text-[#3D8C40] font-bold">•</span>
                <span><strong>Eliminates order chaos</strong> — No more phone calls, sticky notes, or missed orders. Every order is captured, tracked, and accessible.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#3D8C40] font-bold">•</span>
                <span><strong>Saves hours of manual work</strong> — QuickBooks sync eliminates double-entry. Export reports in one click instead of compiling spreadsheets.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#3D8C40] font-bold">•</span>
                <span><strong>Scales with your business</strong> — Multi-tenant architecture means you can onboard new locations or partner businesses without infrastructure changes.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#3D8C40] font-bold">•</span>
                <span><strong>Customers love it</strong> — Mobile-first design means customers can order from anywhere. Personalized links make reordering effortless.</span>
              </li>
            </ul>
          </div>
        </section>

        <footer className="text-center pt-8 border-t border-gray-200">
          <p className="text-gray-500 text-sm">
            Built for produce and food distribution businesses
          </p>
          <p className="text-[#2B5397] font-medium mt-2">
            app.freshorder.io
          </p>
          <p className="mt-4">
            <Link
              href="/account-deletion"
              className="text-xs text-gray-400 underline hover:text-gray-600"
              data-testid="link-account-deletion"
            >
              Request account deletion
            </Link>
          </p>
        </footer>
      </div>
    </div>
  );
}

function FeatureCard({ 
  icon, 
  title, 
  description, 
  highlight = false 
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string; 
  highlight?: boolean;
}) {
  return (
    <Card className={`${highlight ? 'border-[#2B5397]/30 bg-[#2B5397]/5' : 'border-gray-200'}`}>
      <CardContent className="p-4">
        <div className="flex gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            highlight ? 'bg-[#2B5397] text-white' : 'bg-gray-100 text-gray-600'
          }`}>
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
